import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../../components/ui/dialog";
import { api } from "../../../lib/api";
import { toast } from "sonner";

/**
 * v1.14 — Add Item dialog
 *  - rate (per unit) is REQUIRED — every asset must be valued
 *  - initial site + opening qty are REQUIRED — an asset must exist somewhere
 *  - Adding the item to additional sites later uses Adjust Stock
 */
export default function AddItemDialog({ open, onOpenChange, categories, sites, onSuccess }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [unit, setUnit] = useState("");
  const [rate, setRate] = useState("");
  const [seedSite, setSeedSite] = useState("");
  const [seedQty, setSeedQty] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setName(""); setCategory(""); setUnit(""); setRate("");
    setSeedSite(""); setSeedQty(""); setBusy(false);
  };

  const submit = async () => {
    if (!name.trim() || !category || !unit.trim()) {
      toast.error("Name, category and unit are required");
      return;
    }
    const r = parseFloat(rate);
    if (!r || r <= 0) {
      toast.error("Rate per unit is required (must be > 0)");
      return;
    }
    if (!seedSite) {
      toast.error("Initial site is required");
      return;
    }
    const q = parseInt(seedQty, 10);
    if (!q || q <= 0) {
      toast.error("Opening quantity is required (must be > 0)");
      return;
    }
    setBusy(true);
    const body = {
      name: name.trim(),
      category,
      unit: unit.trim(),
      rate_per_unit: r,
      initial_stock: [{ site: seedSite, quantity: q }],
    };
    try {
      await api.post("/inventory/items", body);
      reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (Array.isArray(d) ? d[0]?.msg : null) || "Failed to add item";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="add-item-dialog">
        <DialogHeader>
          <DialogTitle>Add inventory item</DialogTitle>
          <DialogDescription>
            All fields are required. Rate values past breakage; opening site/qty seeds where the asset lives. Additional sites later use Adjust Stock.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Field label="Name" required>
            <input data-testid="add-item-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="e.g. Dinner Plate" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category" required>
              <select data-testid="add-item-category" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
                <option value="">Select…</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Unit" required>
              <input data-testid="add-item-unit" value={unit} onChange={(e) => setUnit(e.target.value)} className={inputCls} placeholder="Pcs / kg / L" />
            </Field>
          </div>
          <Field label="Rate per unit (₹)" required>
            <input data-testid="add-item-rate" type="number" step="0.01" min="0" value={rate} onChange={(e) => setRate(e.target.value)} className={inputCls} placeholder="e.g. 193.20" />
          </Field>
          <div className="pt-2 border-t border-slate-100">
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-2">
              Opening stock <span className="text-red-500">*</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Initial site" required>
                <select data-testid="add-item-seed-site" value={seedSite} onChange={(e) => setSeedSite(e.target.value)} className={inputCls}>
                  <option value="">Select…</option>
                  {sites.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Opening quantity" required>
                <input data-testid="add-item-seed-qty" type="number" min="1" value={seedQty} onChange={(e) => setSeedQty(e.target.value)} className={inputCls} placeholder="e.g. 100" />
              </Field>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">
              Need the item at more sites? Use Adjust Stock from the toolbar after creating it.
            </p>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <button data-testid="add-item-cancel" onClick={() => onOpenChange(false)} className="rounded-full px-4 py-2 text-xs font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">Cancel</button>
          <button data-testid="add-item-submit" onClick={submit} disabled={busy} className="rounded-full px-4 py-2 text-xs font-semibold bg-olive text-white hover:opacity-90 disabled:opacity-50">{busy ? "Adding…" : "Add item"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30";
const Field = ({ label, children, required }) => (
  <label className="block">
    <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </div>
    {children}
  </label>
);
